import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import * as threadAccountApi from '../api/threadAccount';
import { useAuth } from '../auth/AuthContext';
import { useLocalStorage } from '../hooks/useLocalStorage';
import type { ThreadAccountResponse } from '../api/types';

interface ActiveAccountContextValue {
  /** 연동된 전체 Threads 계정 목록 (/all) */
  accounts: ThreadAccountResponse[];
  loading: boolean;
  error: string | null;
  /** 현재 작업 대상으로 선택된 계정 */
  selectedAccountId: number | null;
  selectedAccount: ThreadAccountResponse | null;
  /** 전체 목록 조회 */
  fetchAccounts: () => Promise<ThreadAccountResponse[]>;
  /** 작업 대상 계정 선택 */
  selectAccount: (id: number | null) => void;

  // --- 하위 호환 alias (콘텐츠/말투/발행 페이지에서 사용) ---
  account: ThreadAccountResponse | null;
  accountId: number | null;
  refresh: () => Promise<ThreadAccountResponse[]>;
}

const ActiveAccountContext = createContext<ActiveAccountContextValue | null>(
  null
);

export function ActiveAccountProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [accounts, setAccounts] = useState<ThreadAccountResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const requestId = useRef(0);
  const authenticated = useRef(isAuthenticated);
  authenticated.current = isAuthenticated;
  const [error, setError] = useState<string | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useLocalStorage<
    number | null
  >('auto-threads.selectedAccountId', null);

  const fetchAccounts = useCallback(async () => {
    const currentRequest = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const data = await threadAccountApi.getAllThreadAccounts();
      if (!authenticated.current || currentRequest !== requestId.current) return data;
      setAccounts(data);
      setLoaded(true);
      return data;
    } catch (e) {
      const message =
        e instanceof Error ? e.message : '계정 목록을 불러오지 못했습니다.';
      if (authenticated.current && currentRequest === requestId.current) setError(message);
      throw e;
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, []);

  // 로그인 상태에서 최초 목록 로드
  useEffect(() => {
    if (isAuthenticated) {
      fetchAccounts().catch(() => undefined);
    } else {
      requestId.current += 1;
      setAccounts([]);
      setLoaded(false);
      setLoading(false);
      setSelectedAccountId(null);
    }
  }, [isAuthenticated, fetchAccounts, setSelectedAccountId]);

  // 선택된 계정이 목록에 없으면 선택 해제 (삭제 후 빈 목록 포함)
  useEffect(() => {
    if (!loaded || selectedAccountId == null) return;
    if (!accounts.some((a) => a.id === selectedAccountId)) {
      setSelectedAccountId(null);
    }
  }, [accounts, loaded, selectedAccountId, setSelectedAccountId]);

  const selectAccount = useCallback(
    (id: number | null) => setSelectedAccountId(id),
    [setSelectedAccountId]
  );

  const selectedAccount = useMemo(
    () => accounts.find((a) => a.id === selectedAccountId) ?? null,
    [accounts, selectedAccountId]
  );

  const value = useMemo(
    () => ({
      accounts,
      loading,
      error,
      selectedAccountId,
      selectedAccount,
      fetchAccounts,
      selectAccount,
      // aliases
      account: selectedAccount,
      accountId: selectedAccountId,
      refresh: fetchAccounts,
    }),
    [
      accounts,
      loading,
      error,
      selectedAccountId,
      selectedAccount,
      fetchAccounts,
      selectAccount,
    ]
  );

  return (
    <ActiveAccountContext.Provider value={value}>
      {children}
    </ActiveAccountContext.Provider>
  );
}

export function useActiveAccount() {
  const ctx = useContext(ActiveAccountContext);
  if (!ctx) {
    throw new Error(
      'useActiveAccount must be used within an ActiveAccountProvider'
    );
  }
  return ctx;
}
